import { Trash2, Network, Server } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { CopyButton } from '@/components/ui/copy-button'
import type { NodeDetailRow, NodeStatus } from '@/api/types'
import { formatRelativeTime, formatUptime } from '@/lib/utils'

const statusVariants: Record<NodeStatus, 'default' | 'secondary' | 'destructive' | 'success' | 'warning'> = {
  Active: 'success',
  Pending: 'warning',
  Blocked: 'destructive',
  Rotating: 'default',
  Offline: 'secondary',
}

const protocolColors: Record<string, string> = {
  VlessReality: 'bg-blue-500/10 text-blue-500',
  VlessGrpc: 'bg-purple-500/10 text-purple-500',
  Hysteria2: 'bg-green-500/10 text-green-500',
}

interface NodeCardProps {
  node: NodeDetailRow
  onDelete: () => void
}

export function NodeCard({ node, onDelete }: NodeCardProps) {
  const healthPercent = node.health_score * 100
  const status = node.status as NodeStatus
  const createdAt = node.created_at ? new Date(node.created_at) : null

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-medium">{node.name}</CardTitle>
          <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">
            {node.id.slice(0, 8)}
          </code>
          <CopyButton value={node.id} />
        </div>
        <div className="flex items-center gap-1">
          <Badge variant={statusVariants[status]}>{status}</Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Region</span>
            <div className="font-medium">{node.region}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Provider</span>
            <div className="font-medium">{node.provider}</div>
          </div>
        </div>

        {/* Network Interfaces */}
        {node.interfaces && node.interfaces.length > 0 && (
          <div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
              <Network className="h-3.5 w-3.5" />
              <span>Interfaces</span>
            </div>
            <div className="space-y-2">
              {node.interfaces.map((iface) => (
                <div key={iface.id} className="rounded-md border p-2 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{iface.name}</span>
                    {iface.is_primary && (
                      <Badge variant="secondary" className="text-xs">Primary</Badge>
                    )}
                  </div>
                  <div className="space-y-0.5 text-xs text-muted-foreground">
                    {iface.ipv4 && (
                      <div className="font-mono">{iface.ipv4}</div>
                    )}
                    {iface.ipv6 && (
                      <div className="font-mono break-all">{iface.ipv6}</div>
                    )}
                  </div>
                  {iface.protocols && iface.protocols.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {iface.protocols.map((proto) => (
                        <span
                          key={proto}
                          className={`px-1.5 py-0.5 rounded text-xs font-medium ${protocolColors[proto] || 'bg-muted text-muted-foreground'}`}
                        >
                          {proto}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Local IPv6 Pool Summary */}
        {node.local_pool && (
          <div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
              <Server className="h-3.5 w-3.5" />
              <span>IPv6 Pool</span>
              <span className="font-mono text-xs">/{node.local_pool.prefix_len}</span>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="rounded-md bg-green-500/10 p-1.5">
                <div className="text-lg font-semibold text-green-500">{node.local_pool.ready_count}</div>
                <div className="text-xs text-muted-foreground">Ready</div>
              </div>
              <div className="rounded-md bg-yellow-500/10 p-1.5">
                <div className="text-lg font-semibold text-yellow-500">{node.local_pool.pending_count}</div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
              <div className="rounded-md bg-blue-500/10 p-1.5">
                <div className="text-lg font-semibold text-blue-500">{node.local_pool.active_count}</div>
                <div className="text-xs text-muted-foreground">Active</div>
              </div>
              <div className="rounded-md bg-red-500/10 p-1.5">
                <div className="text-lg font-semibold text-red-500">{node.local_pool.blocked_count}</div>
                <div className="text-xs text-muted-foreground">Blocked</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Health</span>
            <span className="font-medium">{healthPercent.toFixed(0)}%</span>
          </div>
          <Progress value={healthPercent} />
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          {node.last_health_check && (
            <div>Last health check: {formatRelativeTime(node.last_health_check)}</div>
          )}
          <div>Created: {createdAt ? createdAt.toLocaleString() : '—'}</div>
          <div>Running: {createdAt ? formatUptime(createdAt) : '—'}</div>
        </div>
      </CardContent>
    </Card>
  )
}
